import React, { useEffect, useState, useRef } from 'react';
import NavigationBar from './NavigationBar';
import 'swagger-ui-react/swagger-ui.css';
import { useSso } from '../contexts/SsoContext';
import { useLocation } from 'react-router-dom';
import SaveCredentialsButton from './SaveCredentialsButton';

const methodColors = {
  get: '#61affe',
  post: '#49cc90',
  put: '#fca130',
  delete: '#f93e3e',
  patch: '#50e3c2',
  default: '#999',
};

const statusColors = status => {
  if (typeof status === 'number') {
    if (status >= 200 && status < 300) return '#49cc90';
    if (status >= 400 && status < 500) return '#fca130';
    if (status >= 500) return '#f93e3e';
  }
  return '#999';
};

const lockIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" style={{ verticalAlign: 'middle', marginLeft: 4 }}><path fill="#fca130" d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-7V8a6 6 0 0 0-12 0v2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-8-2a4 4 0 0 1 8 0v2H6V8zm10 12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8z"/></svg>
);

// Add custom scrollbar style for curl window
const curlScrollbarStyle = `
.curl-pre-scrollbar::-webkit-scrollbar {
  height: 6px;
  background: #222;
}
.curl-pre-scrollbar::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 4px;
}
.curl-pre-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #666;
}
.curl-pre-scrollbar {
  scrollbar-color: #444 #222;
  scrollbar-width: thin;
}
`;

// Add custom spinner CSS
const spinnerStyle = `
.api-loader-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}
.api-loader-spinner .spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #49cc90;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  margin-bottom: 18px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

const GetApisPage = () => {
  const { loading } = useSso();
  const location = useLocation();
  const [swaggerSpec, setSwaggerSpec] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [domain, setDomain] = useState('');
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [response, setResponse] = useState(null);
  const [entityNames, setEntityNames] = useState([]);
  const [masterAttributes, setMasterAttributes] = useState([]);
  const [customAttributes, setCustomAttributes] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [tryItOut, setTryItOut] = useState(false);
  const [sortByOptions, setSortByOptions] = useState([]);
  const [loadingSpec, setLoadingSpec] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showCredsModal, setShowCredsModal] = useState(false);
  const [credsDraft, setCredsDraft] = useState({ clientId: '', refresh_token: '', Authorization: '', serviceAccount: '', access_token: '' });
  const [saveMsg, setSaveMsg] = useState('');
  const authorizationSetRef = useRef(false);

  // Show loading spinner immediately on route change to /get-apis
  useEffect(() => {
    if (location.pathname === '/get-apis') {
      setLoadingSpec(true);
      setSwaggerSpec(null);
    }
  }, [location.pathname]);

  // Fetch baseUrl from config.json
  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(config => {
        if (config.API_BASE_URL) {
          setBaseUrl(config.API_BASE_URL);
        }
        // Set domain: if localhost, use config.DOMAIN; else use window.location.hostname (without protocol)
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isLocalhost && config.DOMAIN) {
          setDomain(config.DOMAIN.replace("https://", "").replace("http://", "").replace("-api-explorer", ""));
        } else {
          setDomain(window.location.hostname.replace('https://', '').replace('http://', '').replace('-api-explorer', ''));
        }
      });
  }, []);

  // Fetch OpenAPI spec
  useEffect(() => {
    if (baseUrl) {
      const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
      setLoadingSpec(true);
      fetch(`${baseUrl}wolken-secure/v3/api-docs`, {
        credentials: 'include',
        headers: { domain }
      })
        .then(res => res.json())
        .then(spec => {
          // Add hardcoded /oauth/token POST endpoint
          const oauthPath = {
            '/oauth/token': {
              post: {
                tags: ['Generate Token'],
                summary: 'Obtain access token',
                description: 'Use this to get an access token with grant_type and refresh_token',
                operationId: 'getToken',
                parameters: [
                  {
                    name: 'grant_type',
                    in: 'query',
                    description: 'Grant type',
                    required: true,
                    schema: { type: 'string' }
                  },
                  {
                    name: 'refresh_token',
                    in: 'query',
                    description: 'Refresh token',
                    required: true,
                    schema: { type: 'string' }
                  },
                  {
                    name: 'Authorization',
                    in: 'header',
                    description: 'Authorization header',
                    required: true,
                    schema: { type: 'string' }
                  }
                ],
                responses: {
                  '200': {
                    description: 'Success',
                    content: {
                      'application/json': {
                        schema: { type: 'string' }
                      }
                    }
                  },
                  '400': {
                    description: 'Invalid request',
                    content: {
                      'application/json': {
                        schema: { type: 'string' }
                      }
                    }
                  }
                }
              }
            }
          };
          // Merge into spec.paths
          spec.paths = { ...spec.paths, ...oauthPath };
          // Add tag if not present
          if (!spec.tags) spec.tags = [];
          if (!spec.tags.find(t => t.name === 'Generate Token')) {
            spec.tags.push({ name: 'Generate Token', description: 'Oauth Endpoints' });
          }
          setSwaggerSpec(spec);
        })
        .catch(() => setSwaggerSpec(null))
        .finally(() => setLoadingSpec(false));
    }
  }, [baseUrl,domain]);

  // After loading the OpenAPI spec, select the first endpoint of Generate Token by default
  useEffect(() => {
    if (swaggerSpec && !selectedPath && !selectedMethod) {
      // Find the first endpoint under 'Generate Token' tag
      const tag = 'Generate Token';
      let found = false;
      if (swaggerSpec.paths) {
        Object.entries(swaggerSpec.paths).forEach(([path, methods]) => {
          Object.entries(methods).forEach(([method, op]) => {
            if (!found && op.tags && op.tags.includes(tag)) {
              setSelectedPath(path);
              setSelectedMethod(method);
              found = true;
            }
          });
        });
      }
    }
  }, [swaggerSpec, selectedPath, selectedMethod]);

  // Prefill credentials from localStorage if present
  useEffect(() => {
    const creds = JSON.parse(localStorage.getItem('api_explorer_credentials') || '{}');
    if (swaggerSpec && selectedPath && selectedMethod) {
      const paramDefs = swaggerSpec.paths[selectedPath]?.[selectedMethod]?.parameters || [];
      const newVals = {};
      if (paramDefs.find(p => p.name === 'clientId') && creds.clientId) newVals.clientId = creds.clientId;
      if (paramDefs.find(p => p.name === 'refresh_token') && creds.refresh_token) newVals.refresh_token = creds.refresh_token;
      if (paramDefs.find(p => p.name === 'serviceAccount') && creds.serviceAccount) newVals.serviceAccount = creds.serviceAccount;
      // Only set Authorization header if it hasn't been set yet and we're not on /oauth/token endpoint
      // For /oauth/token endpoint, never use access_token for Authorization header
      if (paramDefs.find(p => p.name === 'Authorization') && !authorizationSetRef.current) {
        if (selectedPath === '/oauth/token' && creds.Authorization) {
          newVals.Authorization = creds.Authorization;
          authorizationSetRef.current = true;
        } else if (selectedPath !== '/oauth/token' && creds.access_token) {
          newVals.Authorization = `Bearer ${creds.access_token}`;
          authorizationSetRef.current = true;
        }
      }
      setParamValues(prev => ({ ...prev, ...newVals }));
    }
  }, [swaggerSpec, selectedPath, selectedMethod]);

  // Also prefill on endpoint selection
  const handleSelectEndpoint = (path, method) => {
    setSelectedPath(path);
    setSelectedMethod(method);
    // Reset authorization ref when switching endpoints
    authorizationSetRef.current = false;
    setParamValues(prev => {
      const creds = JSON.parse(localStorage.getItem('api_explorer_credentials') || '{}');
      const paramDefs = swaggerSpec?.paths?.[path]?.[method]?.parameters || [];
      const newVals = {};
      if (paramDefs.find(p => p.name === 'clientId') && creds.clientId) newVals.clientId = creds.clientId;
      if (paramDefs.find(p => p.name === 'refresh_token') && creds.refresh_token) newVals.refresh_token = creds.refresh_token;
      if (paramDefs.find(p => p.name === 'serviceAccount') && creds.serviceAccount) newVals.serviceAccount = creds.serviceAccount;
      // Only set Authorization header if it doesn't already exist in paramValues
      // For /oauth/token endpoint, never use access_token for Authorization header
      if (paramDefs.find(p => p.name === 'Authorization') && !prev.Authorization) {
        if (path === '/oauth/token' && creds.Authorization) {
          newVals.Authorization = creds.Authorization;
        } else if (path !== '/oauth/token' && creds.access_token) {
          newVals.Authorization = `Bearer ${creds.access_token}`;
        }
      }
      // Default entityName if needed
      if (paramDefs.find(p => p.name === 'entityName') && entityNames.length > 0 && !prev.entityName && shouldPrefillEntityName(path)) {
        // Prefer 'request' if available, otherwise use first entity
        const requestEntity = entityNames.find(name => name.toLowerCase() === 'request');
        newVals.entityName = requestEntity || entityNames[0];
      }
      return { ...newVals };
    });
    setResponse(null);
    setTryItOut(false);
  };

  // Fetch entityNames for dropdown
  useEffect(() => {
    if (baseUrl) {
      fetch(`${baseUrl}wolken-secure/report/entity/names`, {
        credentials: 'include',
        headers: { domain }
      })
        .then(res => res.json())
        .then(data => {
          const names = data?.data?.entityNames || [];
          setEntityNames(names);
          // Default entityName if needed
          if (names.length > 0 && swaggerSpec && selectedPath && selectedMethod) {
            const paramDefs = swaggerSpec.paths[selectedPath]?.[selectedMethod]?.parameters || [];
            if (paramDefs.find(p => p.name === 'entityName') && !paramValues.entityName && shouldPrefillEntityName(selectedPath)) {
              // Prefer 'request' if available, otherwise use first entity
              const requestEntity = names.find(name => name.toLowerCase() === 'request');
              setParamValues(prev => ({ ...prev, entityName: requestEntity || names[0] }));
            }
          }
        });
    }
  }, [baseUrl, domain, swaggerSpec, selectedPath, selectedMethod]);

  // Fetch masterAttributes when entityName changes
  useEffect(() => {
    if (baseUrl && paramValues.entityName) {
      fetch(`${baseUrl}wolken-secure/report/entity/master_attributes?entityName=${encodeURIComponent(paramValues.entityName)}`, {
        credentials: 'include',
        headers: { domain }
      })
        .then(res => res.json())
        .then(data => setMasterAttributes(data?.data?.masterAttributes || []));
    }
  }, [baseUrl, paramValues.entityName, domain]);

  // Fetch customAttributes when entityName changes
  useEffect(() => {
    if (baseUrl && paramValues.entityName) {
      fetch(`${baseUrl}wolken-secure/report/entity/custom_attributes?entityName=${encodeURIComponent(paramValues.entityName)}`, {
        credentials: 'include',
        headers: { domain }
      })
        .then(res => res.json())
        .then(data => setCustomAttributes(data?.data?.customAttributes || []));
    }
  }, [baseUrl, paramValues.entityName, domain]);

  // Fetch sortBy options when entityName changes
  useEffect(() => {
    if (baseUrl && paramValues.entityName) {
      fetch(`${baseUrl}wolken-secure/report/entity/master_attributes?entityName=${encodeURIComponent(paramValues.entityName)}`, {
        credentials: 'include',
        headers: { domain }
      })
        .then(res => res.json())
        .then(data => setSortByOptions(data?.data?.masterAttributes || []));
    }
  }, [baseUrl, paramValues.entityName, domain]);

  // Handle parameter value change
  const handleParamChange = (name, value) => {
    setParamValues(prev => ({ ...prev, [name]: value }));
  };

  // Build request URL and options
  const buildRequest = (path, method, params) => {
    let url = baseUrl + 'wolken-secure' + path;
    let options = { method: method.toUpperCase(), headers: { 'Content-Type': 'application/json' } };
    let pathParams = {};
    let queryParams = {};
    let headerParams = {};
    let bodyParams = {};
    if (!swaggerSpec) return { url, options };
    const paramDefs = swaggerSpec.paths[path][method].parameters || [];
    paramDefs.forEach(param => {
      let paramVal = params[param.name];
      // If masterAttributes and array, join as comma-separated string for query param
      if (param.name === 'masterAttributes' && param.in === 'query' && Array.isArray(paramVal)) {
        paramVal = paramVal.join(',');
      }
      if (paramVal === undefined || paramVal === null || paramVal === '') return;
      if (param.in === 'path') {
        pathParams[param.name] = paramVal;
      } else if (param.in === 'query') {
        queryParams[param.name] = paramVal;
      } else if (param.in === 'header') {
        headerParams[param.name] = paramVal;
      } else if (param.in === 'body') {
        bodyParams[param.name] = paramVal;
      }
    });
    // Replace path params
    Object.keys(pathParams).forEach(k => {
      url = url.replace(`{${k}}`, encodeURIComponent(pathParams[k]));
    });
    // Special case: /oauth/token POST (Generate Token)
    if (path === '/oauth/token' && method.toLowerCase() === 'post') {
      // Ensure URL is clean (no query params)
      url = url.split('?')[0];
      // All params go as multipart/form-data, except header params
      const formData = new FormData();
      paramDefs.forEach(param => {
        if (param.in === 'header') return; // skip header params for formData
        const val = params[param.name];
        if (val !== undefined && val !== null && val !== '') {
          formData.append(param.name, val);
        }
      });
      // Only header params go in headers
      const headerParamsOnly = {};
      paramDefs.forEach(param => {
        if (param.in === 'header') {
          const val = params[param.name];
          if (val !== undefined && val !== null && val !== '') {
            headerParamsOnly[param.name] = val;
          }
        }
      });
      options.headers = { ...headerParamsOnly }; // Do not set Content-Type, browser will set it
      options.body = formData;
      // Do NOT add query string for these params
    } else {
      // Add query params
      const queryString = Object.keys(queryParams)
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
        .filter(Boolean)
        .join('&');
      if (queryString) url += (url.includes('?') ? '&' : '?') + queryString;
      // Add headers
      options.headers = { ...options.headers, ...headerParams };
      // Add body
      if (Object.keys(bodyParams).length > 0) {
        options.body = JSON.stringify(bodyParams);
      }
    }
    return { url, options };
  };

  // Execute API call
  const handleExecute = () => {
    if (!selectedPath || !selectedMethod) return;
    setExecuting(true);
    const { url, options } = buildRequest(selectedPath, selectedMethod, paramValues);
    fetch(url, options)
      .then(async res => {
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = text; }
        setResponse({ status: res.status, data });
        
        // If this is /oauth/token POST and response is 200, extract accessToken and update credentials
        if (selectedPath === '/oauth/token' && selectedMethod === 'post' && res.status === 200 && data && data.access_token) {
          const currentCreds = JSON.parse(localStorage.getItem('api_explorer_credentials') || '{}');
          const updatedCreds = { ...currentCreds, access_token: data.access_token };
          localStorage.setItem('api_explorer_credentials', JSON.stringify(updatedCreds));
          
          // Update the paramValues to reflect the new access_token
          setParamValues(prev => ({ ...prev, access_token: data.accessToken }));
        }
      })
      .catch(err => setResponse({ status: 'error', data: err.toString() }))
      .finally(() => setExecuting(false));
  };

  // Generate curl command for the current request
  const generateCurlCommand = () => {
    if (!selectedPath || !selectedMethod || !swaggerSpec) return '';
    const path = selectedPath;
    const method = selectedMethod.toUpperCase();
    const paramDefs = swaggerSpec.paths[path][selectedMethod].parameters || [];
    let url = baseUrl + 'wolken-secure' + path;
    let headers = [];
    let formFields = [];
    let queryParams = [];
    paramDefs.forEach(param => {
      let val = paramValues[param.name];
      // If masterAttributes and array, join as comma-separated string for query param
      if (param.name === 'masterAttributes' && param.in === 'query' && Array.isArray(val)) {
        val = val.join(',');
      }
      if (val === undefined || val === null || val === '') return;
      if (param.in === 'header') {
        headers.push({ name: param.name, value: val });
      } else if (path === '/oauth/token' && method === 'POST') {
        // For /oauth/token POST, treat all non-header params as form fields
        if (Array.isArray(val)) {
          val.forEach(v => formFields.push({ name: param.name, value: v }));
        } else {
          formFields.push({ name: param.name, value: val });
        }
      } else if (param.in === 'query') {
        if (Array.isArray(val)) {
          val.forEach(v => queryParams.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(v)}`));
        } else {
          queryParams.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(val)}`);
        }
      } else if (param.in === 'path') {
        url = url.replace(`{${param.name}}`, encodeURIComponent(val));
      } else {
        // For form fields (body, formData, etc)
        if (Array.isArray(val)) {
          val.forEach(v => formFields.push({ name: param.name, value: v }));
        } else {
          formFields.push({ name: param.name, value: val });
        }
      }
    });
    // For /oauth/token POST, do not add query params
    let urlWithQuery = url;
    if (!(path === '/oauth/token' && method === 'POST') && queryParams.length > 0) {
      urlWithQuery += (urlWithQuery.includes('?') ? '&' : '?') + queryParams.join('&');
    }
    // Build curl command (bash, multi-line with \)
    let curl = `curl -X ${method} '${urlWithQuery}'`;
    const curlLines = [curl];
    // Add headers
    headers.forEach(h => {
      curlLines.push(`  -H '${h.name}: ${h.value}'`);
    });
    // Add form fields for /oauth/token POST
    if (path === '/oauth/token' && method === 'POST') {
      formFields.forEach(f => {
        curlLines.push(`  -F '${f.name}=${f.value}'`);
      });
    } else if (formFields.length > 0) {
      // For other endpoints, send as JSON
      curlLines.push(`  -H 'Content-Type: application/json'`);
      curlLines.push(`  -d '${JSON.stringify(Object.fromEntries(formFields.map(f => [f.name, f.value])))}'`);
    }
    // Join with line breaks and trailing backslash (except last line)
    return curlLines.map((line, idx) => idx < curlLines.length - 1 ? line + ' \\' : line).join('\n');
  };

  // Copy to clipboard helper
  const handleCopyCurl = (curlCmd) => {
    if (!curlCmd) return;
    navigator.clipboard.writeText(curlCmd).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1200);
    });
  };

  const openCredsModal = () => {
    setCredsDraft({
      clientId: paramValues.clientId || '',
      refresh_token: paramValues.refresh_token || '',
      Authorization: paramValues.Authorization || '',
      serviceAccount: paramValues.serviceAccount || '',
      access_token: paramValues.access_token || '',
    });
    setShowCredsModal(true);
  };
  const closeCredsModal = () => setShowCredsModal(false);
  const handleCredsDraftChange = (e) => {
    setCredsDraft({ ...credsDraft, [e.target.name]: e.target.value });
  };
  const handleSaveCredsModal = () => {
    localStorage.setItem('api_explorer_credentials', JSON.stringify(credsDraft));
    setShowCredsModal(false);
    setSaveMsg('Credentials saved!');
    // Prefill fields immediately after save (except Authorization header to prevent refresh)
    if (swaggerSpec && selectedPath && selectedMethod) {
      const paramDefs = swaggerSpec.paths[selectedPath]?.[selectedMethod]?.parameters || [];
      const newVals = {};
      if (paramDefs.find(p => p.name === 'clientId') && credsDraft.clientId) newVals.clientId = credsDraft.clientId;
      if (paramDefs.find(p => p.name === 'refresh_token') && credsDraft.refresh_token) newVals.refresh_token = credsDraft.refresh_token;
      if (paramDefs.find(p => p.name === 'serviceAccount') && credsDraft.serviceAccount) newVals.serviceAccount = credsDraft.serviceAccount;
      // Don't update Authorization header to prevent automatic refresh
      setParamValues(prev => ({ ...prev, ...newVals }));
    }
    setTimeout(() => setSaveMsg(''), 1500);
  };

  // Handler to refresh parameter fields after credentials are saved
  const handleCredentialsSaved = () => {
    if (swaggerSpec && selectedPath && selectedMethod) {
      const creds = JSON.parse(localStorage.getItem('api_explorer_credentials') || '{}');
      const paramDefs = swaggerSpec.paths[selectedPath]?.[selectedMethod]?.parameters || [];
      const newVals = {};
      if (paramDefs.find(p => p.name === 'clientId') && creds.clientId) newVals.clientId = creds.clientId;
      if (paramDefs.find(p => p.name === 'refresh_token') && creds.refresh_token) newVals.refresh_token = creds.refresh_token;
      if (paramDefs.find(p => p.name === 'serviceAccount') && creds.serviceAccount) newVals.serviceAccount = creds.serviceAccount;
      // Don't update Authorization header to prevent automatic refresh
      setParamValues(prev => ({ ...prev, ...newVals }));
    }
  };

  // Helper to check if entityName should be shown for a given endpoint
  const shouldShowEntityName = (path, method) => {
    // Hide for /rest-api/datatypes/{entityName}
    return !(path && path.startsWith('/rest-api/datatypes/') && path.includes('{entityName}'));
  };

  // Helper to check if etityName should be shown for a given endpoint
  const shouldShowEtityName = (path, method) => {
    // Hide for /rest-api/datatypes/{entityName}
    return !(path && path.startsWith('/rest-api/datatypes/') && path.includes('{entityName}'));
  };

  // Helper to check if entityName should be prefilled for a given endpoint
  const shouldPrefillEntityName = (path) => {
    if (!path) return false;
    // Match /rest-api/{entityName}/{entityId}
    if (/^\/rest-api\/[^/]+\/[^/]+$/.test(path)) return true;
    // Match /rest-api/labels/{entityName}
    if (/^\/rest-api\/labels\/[^/]+$/.test(path)) return true;
    // Match /rest-api/datatypes/{entityName}
    if (/^\/rest-api\/datatypes\/[^/]+$/.test(path)) return true;
    // Match /rest-api/count/{entityName}
    if (/^\/rest-api\/count\/[^/]+$/.test(path)) return true;
    return false;
  };

  // Render parameter input
  const renderParamInput = (param) => {
    // Prefill domain field with domain state if localhost, else cleaned window.location.hostname
    if (param.name === 'domain') {
      let domainValue = '';
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost && domain) {
        domainValue = domain.replace('https://', '').replace('http://', '').replace('-api-explorer', '');
      } else {
        domainValue = window.location.hostname.replace('https://', '').replace('http://', '').replace('-api-explorer', '');
      }
      if (paramValues[param.name] !== domainValue) {
        setParamValues(prev => ({ ...prev, [param.name]: domainValue }));
      }
      return (
        <input
          className="parameter__input"
          type="text"
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle', background: '#f3f3f3' }}
          value={domainValue}
          disabled
        />
      );
    }
    // Special handling for /oauth/token POST grant_type
    if (
      param.name === 'grant_type' &&
      selectedPath === '/oauth/token' &&
      selectedMethod === 'post'
    ) {
      // Prefill and lock value
      if (paramValues[param.name] !== 'refresh_token') {
        setParamValues(prev => ({ ...prev, [param.name]: 'refresh_token' }));
      }
      return (
        <input
          className="parameter__input"
          type="text"
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle', background: '#f3f3f3' }}
          value={'refresh_token'}
          disabled
        />
      );
    }
    const value = paramValues[param.name] || '';
    // Info icon for 'query' field
    const infoIcon = (
      <span
        style={{
          display: 'inline-block',
          marginLeft: 6,
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
        title="Open API Explorer Guide"
        onClick={e => {
          e.preventDefault();
          window.open('/Wolken API Explorer Guide.docx.pdf', '_blank');
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }}>
          <circle cx="12" cy="12" r="10" fill="#61affe" />
          <text x="12" y="16" textAnchor="middle" fontSize="13" fill="#fff" fontFamily="Arial" fontWeight="bold">i</text>
        </svg>
      </span>
    );
    if (param.name === 'entityName') {
      return (
        <select
          className="parameter__input"
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
          value={value}
          disabled={!tryItOut}
          onChange={e => handleParamChange(param.name, e.target.value)}
        >
          <option value="">Select entityName</option>
          {entityNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      );
    }
    if (param.name === 'masterAttributes') {
      return (
        <select
          className="parameter__input"
          multiple
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
          disabled={!tryItOut}
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={e => {
            const options = Array.from(e.target.selectedOptions, option => option.value);
            handleParamChange(param.name, options);
          }}
        >
          {masterAttributes.map(attr => (
            <option key={attr} value={attr}>{attr}</option>
          ))}
        </select>
      );
    }
    if (param.name === 'customAttributes') {
      return (
        <select
          className="parameter__input"
          multiple
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
          disabled={!tryItOut}
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={e => {
            const options = Array.from(e.target.selectedOptions, option => option.value);
            handleParamChange(param.name, options);
          }}
        >
          {customAttributes.map(attr => (
            <option key={attr} value={attr}>{attr}</option>
          ))}
        </select>
      );
    }
    if (param.name === 'sortBy') {
      return (
        <select
          className="parameter__input"
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
          value={value}
          disabled={!tryItOut}
          onChange={e => handleParamChange(param.name, e.target.value)}
        >
          <option value="">Select sortBy</option>
          {sortByOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (param.schema && param.schema.enum) {
      return (
        <select
          className="parameter__input"
          style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
          value={value}
          disabled={!tryItOut}
          onChange={e => handleParamChange(param.name, e.target.value)}
        >
          <option value="">Select {param.name}</option>
          {param.schema.enum.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    // For 'query' field, add info icon
    if (param.name === 'query') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
          <input
            className="parameter__input"
            type="text"
            style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle', flex: 1 }}
            value={value}
            disabled={!tryItOut}
            onChange={e => handleParamChange(param.name, e.target.value)}
            placeholder={param.description || param.name}
          />
          {infoIcon}
        </span>
      );
    }
    return (
      <input
        className="parameter__input"
        type="text"
        style={{ minHeight: 32, padding: '4px 8px', fontSize: 15, verticalAlign: 'middle' }}
        value={value}
        disabled={!tryItOut}
        onChange={e => handleParamChange(param.name, e.target.value)}
        placeholder={param.description || param.name}
      />
    );
  };

  // Render parameter table
  const renderParameterTable = (params) => (
    <>
      <table className="parameters-table" style={{ width: '100%', marginBottom: 16, borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', fontWeight: 500 }}>Name</th>
            <th style={{ textAlign: 'left', fontWeight: 500 }}>In</th>
            <th style={{ textAlign: 'left', fontWeight: 500 }}>Type</th>
            <th style={{ textAlign: 'left', fontWeight: 500 }}>Description</th>
            <th style={{ textAlign: 'left', fontWeight: 500, minWidth: 180 }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {params.filter(param => param.name !== 'etityName' || shouldShowEtityName(selectedPath, selectedMethod)).map(param => (
            <tr key={param.name} style={{ verticalAlign: 'middle', height: 48 }}>
              <td>
                <span className="parameter__name" style={{ fontWeight: 500 }}>
                  {param.name}
                  {param.required && <span style={{ color: '#f93e3e', marginLeft: 2 }}>*</span>}
                </span>
              </td>
              <td><span className="parameter__in" style={{ background: '#e8e8e8', borderRadius: 4, padding: '2px 6px', fontSize: 12 }}>{param.in}</span></td>
              <td><span className="parameter__type">{param.schema ? param.schema.type : param.type}</span></td>
              <td style={{ fontSize: 13 }}>{param.description}</td>
              <td style={{ verticalAlign: 'middle', padding: '6px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
                  {renderParamInput(param)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  // Render endpoint details
  const renderEndpointDetails = () => {
    if (!swaggerSpec || !selectedPath || !selectedMethod) return null;
    const op = swaggerSpec.paths[selectedPath][selectedMethod];
    const params = op.parameters || [];
    const secured = op.security && op.security.length > 0;
    const curlCmd = generateCurlCommand();
    return (
      <div className="swagger-ui">
        <div className={`opblock opblock-${selectedMethod}`}
          style={{ border: `2px solid ${methodColors[selectedMethod] || methodColors.default}` }}>
          <div className={`opblock-summary opblock-summary-${selectedMethod}`}
            style={{ background: methodColors[selectedMethod] || methodColors.default, color: '#fff', borderRadius: '4px 4px 0 0', padding: '12px 16px', display: 'flex', alignItems: 'center' }}>
            <span className="opblock-summary-method" style={{ background: '#fff', color: methodColors[selectedMethod] || methodColors.default, borderRadius: 4, fontWeight: 700, fontSize: 14, padding: '2px 10px', marginRight: 12 }}>{selectedMethod.toUpperCase()}</span>
            <span className="opblock-summary-path" style={{ fontFamily: 'monospace', fontSize: 16, marginRight: 12 }}>{selectedPath}</span>
            {secured && lockIcon}
            <span className="opblock-summary-description" style={{ marginLeft: 16, fontSize: 15, color: '#fff', opacity: 0.9 }}>{op.summary || op.description}</span>
          </div>
          <div className="opblock-body" style={{ background: '#fff', padding: 24, borderRadius: '0 0 4px 4px' }}>
            {/* Save Credentials Button inside endpoint details */}
            <div style={{ marginBottom: 16 }}>
              <SaveCredentialsButton onSave={handleCredentialsSaved} />
            </div>
            <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Parameters</span>
              {!tryItOut && (
                <button className="btn try-out__btn" style={{ background: '#fff', color: '#49cc90', border: '2px solid #49cc90', borderRadius: 4, fontWeight: 600, padding: '6px 18px', marginLeft: 12 }} onClick={() => setTryItOut(true)}>Try it out</button>
              )}
              {tryItOut && (
                <button className="btn cancel" style={{ background: '#fff', color: '#f93e3e', border: '2px solid #f93e3e', borderRadius: 4, fontWeight: 600, padding: '6px 18px', marginLeft: 12 }} onClick={() => setTryItOut(false)}>Cancel</button>
              )}
            </div>
            {params.length === 0 && <div className="parameter__empty">No parameters</div>}
            {params.length > 0 && renderParameterTable(params)}
            {/* Curl command preview with copy icon */}
            {curlCmd && (
              <div style={{ margin: '24px 0', position: 'relative' }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Curl:</div>
                <div style={{ position: 'relative', maxWidth: 900, width: '100%', overflowX: 'auto' }}>
                  <button
                    onClick={() => handleCopyCurl(curlCmd)}
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: 'rgba(34,34,34,0.85)',
                      color: '#333',
                      border: 'none',
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      zIndex: 2,
                    }}
                    title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
                  >
                    {copySuccess ? (
                      // Checkmark icon
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#49cc90" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9.5 17 4 11.5" /></svg>
                    ) : (
                      // Professional clipboard icon
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="4" width="12" height="16" rx="2"/>
                        <path d="M9 2h6a1 1 0 0 1 1 1v1H8V3a1 1 0 0 1 1-1z"/>
                      </svg>
                    )}
                  </button>
                  <pre style={{
                    background: '#222',
                    color: '#fff',
                    padding: '1em',
                    borderRadius: 4,
                    fontSize: 14,
                    overflowX: 'auto',
                    marginTop: 0,
                    position: 'relative',
                    minHeight: 60,
                    maxWidth: 900,
                    width: '100%',
                    whiteSpace: 'pre',
                    display: 'block',
                    scrollbarColor: '#888 #222',
                    scrollbarWidth: 'thin',
                  }}
                  className="curl-pre-scrollbar"
                  >
                    {curlCmd}
                  </pre>
                </div>
              </div>
            )}
            <div style={{ margin: '16px 0' }}>
              {tryItOut && (
                <button className="btn execute opblock-control__btn" style={{ background: '#49cc90', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, padding: '6px 18px', marginRight: 12 }} onClick={handleExecute} disabled={executing}>
                  {executing ? 'Executing...' : 'Execute'}
                </button>
              )}
            </div>
            {response && (
              <div className="responses-table" style={{ marginTop: 24, maxHeight: 400, overflow: 'auto', background: '#222', borderRadius: 4, padding: 0 }}>
                <div className="responses-header" style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, padding: '16px 16px 0 16px', background: 'transparent' }}>Response</div>
                <pre className="responses-content" style={{ background: statusColors(response.status), color: '#fff', padding: '1em', borderRadius: '4px', fontSize: 15, maxHeight: 350, overflow: 'auto', margin: 0 }}>
                  {typeof response.data === 'object' ? JSON.stringify(response.data, null, 2) : String(response.data)}
                </pre>
                <div className="responses-status" style={{ fontWeight: 500, marginTop: 8, padding: '0 16px 16px 16px' }}>Status: <span style={{ color: statusColors(response.status) }}>{response.status}</span></div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Group endpoints by tags for sidebar accordion
  const getEndpointsByTag = () => {
    if (!swaggerSpec) return {};
    const tagMap = {};
    Object.keys(swaggerSpec.paths).forEach(path => {
      Object.keys(swaggerSpec.paths[path]).forEach(method => {
        const op = swaggerSpec.paths[path][method];
        const tags = op.tags || ['default'];
        tags.forEach(tag => {
          if (!tagMap[tag]) tagMap[tag] = [];
          tagMap[tag].push({ path, method, summary: op.summary });
        });
      });
    });
    return tagMap;
  };

  // Render sidebar with all endpoints always visible (not collapsible)
  const renderSidebar = () => {
    if (!swaggerSpec) return null;
    const tagMap = getEndpointsByTag();
    // Sort tags so 'Generate Token' is first, then the rest
    const sortedTags = Object.keys(tagMap).sort((a, b) => {
      if (a === 'Generate Token') return -1;
      if (b === 'Generate Token') return 1;
      return 0;
    });
    return (
      <div className="swagger-sidebar" style={{ width: 340, float: 'left', background: '#fafafa', height: '100vh', overflowY: 'auto', borderRight: '1px solid #eee', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ padding: '1em', fontWeight: 'bold', fontSize: 20, color: '#333', borderBottom: '1px solid #eee' }}>Endpoints</div>
        {sortedTags.map(tag => (
          <div key={tag} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 16, padding: '10px 16px', background: '#f3f3f3', borderRadius: 4, border: '1px solid #e8e8e8', marginBottom: 2 }}>
              {tag}
            </div>
            <div style={{ marginLeft: 8 }}>
              {tagMap[tag].map(({ path, method, summary }) => (
                <div
                  key={method + path}
                  className={`opblock opblock-${method} ${selectedPath === path && selectedMethod === method ? 'opblock-selected' : ''}`}
                  style={{ cursor: 'pointer', margin: '0 0 2px 0', padding: '8px 12px', borderRadius: 4, border: selectedPath === path && selectedMethod === method ? '2px solid #49cc90' : '1px solid #ddd', background: selectedPath === path && selectedMethod === method ? '#f0fff4' : '#fff', display: 'flex', alignItems: 'center' }}
                  onClick={() => handleSelectEndpoint(path, method)}
                >
                  <span className="opblock-summary-method" style={{ background: methodColors[method] || methodColors.default, color: '#fff', borderRadius: 4, fontWeight: 700, fontSize: 13, padding: '2px 10px', marginRight: 10 }}>{method.toUpperCase()}</span>
                  <span className="opblock-summary-path" style={{ fontFamily: 'monospace', fontSize: 15, marginRight: 8 }}>{path}</span>
                  {summary && (
                    <span
                      style={{
                        color: '#bbb',
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 120,
                        marginLeft: 4,
                        display: 'inline-block',
                        verticalAlign: 'middle',
                      }}
                      title={summary}
                    >
                      {summary}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <NavigationBar />
      <style>{curlScrollbarStyle}</style>
      <style>{spinnerStyle}</style>
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: '72px', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {renderSidebar()}
          <div style={{ flex: 1, padding: '2em', minHeight: '100vh', background: '#f8f8f8' }}>
            {(loading || loadingSpec) && (
              <div className="api-loader-spinner">
                <div className="spinner"></div>
                <div style={{ color: '#888', fontSize: '1.2rem', marginTop: 8 }}>Loading...</div>
              </div>
            )}
            {!loading && !loadingSpec && swaggerSpec && renderEndpointDetails()}
            {!loading && !loadingSpec && !swaggerSpec && <div>API documentation not available.</div>}
          </div>
        </div>
      </div>
    </>
  );
};

export default GetApisPage;